/**
 * AI Module - Journey Output Schema v2.0
 *
 * Zod schema for validating the structured JSON output from the LLM.
 * This is the strict contract between LLM output and UI rendering.
 * The UI renders this JSON directly - no LLM content outside this schema.
 * 
 * v2.0 Updates:
 * - Added heroStats for "wow" moment display
 * - Added taxSavingsHighlight for quantified savings
 * - Added aiInsights for personalized observations
 * - Added scenarioComparison for decision support
 * - Enhanced action schema with estimatedSavings
 */

import { z } from 'zod';

// ============================================
// NEW: Hero Stats (The "Wow" Moment)
// ============================================

export const heroStatsSchema = z.object({
  // Tax savings highlight
  totalTaxSavings: z.object({
    amountINR: z.number().min(0).default(0),
    formatted: z.string().default('₹0'), // "₹42L" or "₹3.2Cr"
    comparedTo: z.string().default('vs standard approach'), // "vs returning without optimization"
  }).default({ amountINR: 0, formatted: '₹0', comparedTo: 'vs standard approach' }),
  // Time to FIRE
  timeToFIRE: z.object({
    months: z.number().int().min(0).default(0),
    formatted: z.string().default('Calculating...'), // "24 months" or "Already achieved!"
    status: z.enum(['achieved', 'on_track', 'at_risk']).default('on_track'),
  }).default({ months: 0, formatted: 'Calculating...', status: 'on_track' }),
  // Confidence score
  confidence: z.object({
    score: z.number().min(0).max(100).default(50), // 0-100%
    level: z.enum(['high', 'medium', 'low']).default('medium'),
    explanation: z.string().default('Based on available data'), // "Based on complete financial data"
  }).default({ score: 50, level: 'medium', explanation: 'Based on available data' }),
  // RNOR benefit
  rnorBenefit: z.object({
    eligible: z.boolean().default(false),
    years: z.number().int().min(0).max(4).default(0),
    impactDescription: z.string().default('RNOR eligibility pending calculation'), // "2-year tax-free foreign income window"
  }).default({ eligible: false, years: 0, impactDescription: 'RNOR eligibility pending calculation' }),
  // Social Security eligibility (USA only) - from preComputedJourneyData.socialSecurity
  ssEligibility: z.object({
    eligible: z.boolean().default(false),
    yearsToQualify: z.number().int().min(0).default(0),
    lifetimeValueFormatted: z.string().optional(), // "$432,000" or "₹3.6Cr"
    recommendation: z.string().default(''), // "You're 2 years away from qualifying..."
    isHighValueOpportunity: z.boolean().optional(),
  }).optional(),
  // Personalized greeting/headline
  personalizedHeadline: z.string().default('Your Personalized Return Strategy'), // "Paras, here's your optimized return strategy"
  personalizedSubheadline: z.string().default('Here are the key insights for your return to India'), // "I've identified 3 key opportunities..."
});

// ============================================
// NEW: Tax Savings Breakdown
// ============================================

export const taxSavingsItemSchema = z.object({
  category: z.string().default('Tax Savings'), // "RNOR Foreign Income", "401k Timing", etc.
  amountINR: z.number().min(0).default(0),
  formatted: z.string().default('₹0'), // "₹18L"
  description: z.string().default('Tax optimization opportunity'), // "Tax-free withdrawal during RNOR window"
  timing: z.string().default('During return period'), // "During RNOR (2027-2030)"
  confidence: z.enum(['high', 'medium', 'estimated']).default('medium'),
});

export const taxSavingsHighlightSchema = z.object({
  totalSavingsINR: z.number().min(0).default(0),
  totalFormatted: z.string().default('₹0'), // "₹42L" - default to avoid undefined
  savingsBreakdown: z.array(taxSavingsItemSchema).default([]), // Remove min(1) to avoid validation failure
  methodology: z.string().default('Based on your profile data'),
  disclaimer: z.string().default('Estimates based on current tax laws. Consult a CA for accurate figures.'),
});

// ============================================
// NEW: AI Insights (Personalized Observations)
// ============================================

export const aiInsightTypeSchema = z.enum([
  'opportunity',  // 💡 Something they can gain
  'warning',      // ⚠️ Something to watch out for  
  'confirmation', // ✅ Something already going well
  'tip',          // 💬 General advice
  'critical',     // 🔴 Urgent action needed
]);

export const aiInsightSchema = z.object({
  id: z.string().default('insight_1'),
  type: aiInsightTypeSchema.default('tip'),
  title: z.string().max(60).default('Financial Insight'), // "Your 401k is your biggest opportunity"
  insight: z.string().max(300).default('Review your financial strategy'), // The personalized observation
  specificAmount: z.string().optional(), // "₹18L" - if quantifiable
  relatedAsset: z.string().optional(), // "401k", "RSUs", etc.
  actionRequired: z.boolean().default(false),
  urgency: z.enum(['immediate', 'before_return', 'during_rnor', 'ongoing']).optional(),
  relatedActionId: z.string().optional(), // Links to an action item
});

// ============================================
// NEW: Scenario Comparison
// ============================================

export const scenarioMetricsSchema = z.object({
  rnorYears: z.number().int().min(0).max(4).default(0),
  taxSavingsINR: z.number().min(0).default(0),
  taxSavingsFormatted: z.string().default('₹0'),
  additionalCorpusINR: z.number().default(0), // Can be negative if accelerating
  additionalCorpusFormatted: z.string().default('₹0'),
  riskLevel: z.enum(['low', 'medium', 'high']).default('medium'),
  riskExplanation: z.string().default('Standard risk profile'),
});

export const scenarioSchema = z.object({
  id: z.string().default('scenario_1'),
  name: z.string().default('Return Scenario'), // "Return as planned", "Delay by 1 year"
  returnDate: z.string().default('TBD'), // "April 2027"
  returnFY: z.string().default('TBD'), // "FY 2027-28"
  isRecommended: z.boolean().default(false),
  isCurrentPlan: z.boolean().default(false),
  metrics: scenarioMetricsSchema.default({
    rnorYears: 0,
    taxSavingsINR: 0,
    taxSavingsFormatted: '₹0',
    additionalCorpusINR: 0,
    additionalCorpusFormatted: '₹0',
    riskLevel: 'medium',
    riskExplanation: 'Standard risk profile',
  }),
  pros: z.array(z.string()).max(3).default([]),
  cons: z.array(z.string()).max(3).default([]),
});

export const scenarioComparisonSchema = z.object({
  scenarios: z.array(scenarioSchema).min(2).max(4),
  recommendedScenarioId: z.string(),
  recommendationRationale: z.string(), // "April 2027 balances tax savings with corpus building"
  keyTradeoff: z.string(), // "Earlier return = more RNOR years but less corpus"
});

// ============================================
// Summary Section (Enhanced)
// ============================================

export const journeySummarySchema = z.object({
  headline: z.string().max(100),
  subheadline: z.string().max(200),
  confidenceLevel: z.enum(['high', 'medium', 'low']),
  confidenceExplanation: z.string(),
});

// ============================================
// Timeline Section (Enhanced)
// ============================================

export const timelineMilestoneSchema = z.object({
  id: z.string().default('milestone_1'),
  date: z.string().default('TBD'),
  label: z.string().default('Milestone'),
  type: z.enum(['start', 'return', 'rnor_end', 'fire_target', 'milestone']).default('milestone'),
  description: z.string().optional(),
});

export const journeyTimelineSchema = z.object({
  startDate: z.string(), // "January 2026"
  targetReturnDate: z.string(), // "March 2028"
  targetReturnFY: z.string().optional(), // "FY 2028-29" - clarity for Indian FY
  rnorStartDate: z.string().optional(), // "April 2027" - when RNOR begins
  rnorEndDate: z.string().optional(), // "March 2031"
  // Key milestone dates for timeline visualization
  sixMonthsBefore: z.string().optional(), // "October 2026" - prep milestone
  threeMonthsBefore: z.string().optional(), // "January 2027" - liquidation milestone
  fireTargetDate: z.string().optional(), // "June 2032"
  totalMonths: z.number().int().min(0),
  // NEW: Milestones for visual timeline
  milestones: z.array(timelineMilestoneSchema).optional(),
});

// ============================================
// Action Item Schema (Enhanced)
// ============================================

export const actionCategorySchema = z.enum([
  'tax',
  'investment',
  'compliance',
  'account',
  'insurance',
  'lifestyle',
  'timing',
  'documentation',
]);

export const actionPrioritySchema = z.enum(['critical', 'high', 'medium']);

export const journeyActionSchema = z.object({
  id: z.string(),
  sequence: z.number().int().min(1),
  title: z.string().max(80),
  description: z.string().max(200),
  rationale: z.string(), // "Why this matters"

  // Categorization
  category: actionCategorySchema,
  priority: actionPrioritySchema,

  // Timing
  suggestedTiming: z.string(), // "Q1 2027" or "Before March 2028"
  deadline: z.string().optional(), // Hard deadline if exists
  isTimeSensitive: z.boolean(),

  // NEW: Quantified impact
  estimatedSavingsINR: z.number().optional(), // Specific savings amount
  estimatedSavingsFormatted: z.string().optional(), // "₹8.5L"
  impactDescription: z.string().optional(), // "Could save 20% on capital gains"

  // Trust signals
  requiresCAReview: z.boolean(),
  caReviewReason: z.string().optional(), // Required if requiresCAReview = true
  sourceReference: z.string().optional(), // "RNOR - Section 6(6) IT Act"

  // Linkage to Fact Pack (for auditability)
  factPackSource: z.string(), // "priorityActions[2]" or "liquidationPlan[0]"
});

// ============================================
// Phase Schema (Enhanced)
// ============================================

export const phaseStatusSchema = z.enum(['current', 'upcoming', 'future']);

export const journeyPhaseSchema = z.object({
  id: z.string(), // "phase_1_pre_return"
  sequence: z.number().int().min(1),
  name: z.string(), // "Pre-Return Preparation"
  dateRange: z.object({
    start: z.string(), // "January 2026"
    end: z.string(), // "September 2027"
  }),
  description: z.string(), // 2-3 sentence overview
  status: phaseStatusSchema,
  actions: z.array(journeyActionSchema),
  // NEW: Phase summary stats
  totalActions: z.number().int().optional(),
  criticalActions: z.number().int().optional(),
  estimatedSavingsINR: z.number().optional(), // Total savings from this phase
});

// ============================================
// Key Decision Points
// ============================================

export const reviewTypeSchema = z.enum(['ca', 'tax_attorney', 'financial_advisor']);

export const keyDecisionPointSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  suggestedDecisionDate: z.string(),
  options: z.array(z.string()).optional(), // If there are choices
  requiresProfessionalReview: z.boolean(),
  reviewType: reviewTypeSchema,
  factPackSource: z.string(),
  // NEW: Impact quantification
  impactIfDelayed: z.string().optional(), // "Missing this could cost ₹5L"
});

// ============================================
// Assumptions
// ============================================

export const assumptionSourceSchema = z.enum([
  'user_input',
  'system_default',
  'calculated',
]);

export const journeyAssumptionSchema = z.object({
  category: z.string(), // "Tax Rules", "Market", "Personal"
  assumption: z.string(),
  source: assumptionSourceSchema,
});

// ============================================
// Disclaimers
// ============================================

export const journeyDisclaimersSchema = z.object({
  standard: z.string(), // Legal disclaimer
  itemsRequiringCAReview: z.array(z.string()), // List of CA-flagged items
  dataLimitations: z.array(z.string()), // What we didn't have data for
});

// ============================================
// NEW: Liquidation Sequence (Stored in Journey)
// ============================================

export const liquidationSequenceItemSchema = z.object({
  id: z.string(),
  sequence: z.number(),
  asset: z.string(),
  action: z.string(),
  timing: z.enum(['before_move', 'during_rnor', 'post_rnor']),
  timingDescription: z.string(),
  reason: z.string(),
  estimatedValueUSD: z.number(),
  estimatedValueINR: z.number(),
  taxImplication: z.string(),
  requiresCAReview: z.boolean(),
});

// ============================================
// Main Journey Output Schema v2.0
// ============================================

export const journeyOutputSchema = z.object({
  // Meta
  schemaVersion: z.literal('2.0'),
  generatedAt: z.string().datetime(),

  // NEW: Hero section for "wow" moment
  heroStats: heroStatsSchema,

  // NEW: Tax savings with breakdown
  taxSavingsHighlight: taxSavingsHighlightSchema,

  // NEW: AI-generated insights
  aiInsights: z.array(aiInsightSchema).min(2).max(6),

  // NEW: Scenario comparison for decision support
  scenarioComparison: scenarioComparisonSchema.optional(),

  // NEW: Liquidation sequence (stored in journey for cache consistency)
  liquidationSequence: z.array(liquidationSequenceItemSchema).optional(),

  // Core Sections (existing, enhanced)
  summary: journeySummarySchema,
  timeline: journeyTimelineSchema,
  phases: z.array(journeyPhaseSchema).min(1).max(5),
  keyDecisionPoints: z.array(keyDecisionPointSchema),
  assumptions: z.array(journeyAssumptionSchema),
  disclaimers: journeyDisclaimersSchema,

  // Internal flags (not rendered to user)
  _isFallback: z.boolean().optional(), // Flag for fallback journey
  _warnings: z.array(z.string()).optional(), // Hallucination warnings
  _processingTimeMs: z.number().optional(),
  caveats: z.array(z.string()).optional(), // From clarifier pass
});

// ============================================
// Backward Compatibility: v1.0 Schema
// ============================================

export const journeyOutputSchemaV1 = z.object({
  schemaVersion: z.literal('1.0'),
  generatedAt: z.string().datetime(),
  summary: journeySummarySchema,
  timeline: journeyTimelineSchema,
  phases: z.array(journeyPhaseSchema).min(1).max(5),
  keyDecisionPoints: z.array(keyDecisionPointSchema),
  assumptions: z.array(journeyAssumptionSchema),
  disclaimers: journeyDisclaimersSchema,
  _isFallback: z.boolean().optional(),
  _warnings: z.array(z.string()).optional(),
  _processingTimeMs: z.number().optional(),
  caveats: z.array(z.string()).optional(),
});

// Union schema that accepts both versions
export const journeyOutputSchemaAny = z.union([
  journeyOutputSchema,
  journeyOutputSchemaV1,
]);

// ============================================
// Type Exports
// ============================================

export type JourneyOutput = z.infer<typeof journeyOutputSchema>;
export type JourneyOutputV1 = z.infer<typeof journeyOutputSchemaV1>;
export type JourneySummary = z.infer<typeof journeySummarySchema>;
export type JourneyTimeline = z.infer<typeof journeyTimelineSchema>;
export type JourneyPhase = z.infer<typeof journeyPhaseSchema>;
export type JourneyAction = z.infer<typeof journeyActionSchema>;
export type KeyDecisionPoint = z.infer<typeof keyDecisionPointSchema>;
export type JourneyAssumption = z.infer<typeof journeyAssumptionSchema>;
export type JourneyDisclaimers = z.infer<typeof journeyDisclaimersSchema>;
export type ActionCategory = z.infer<typeof actionCategorySchema>;
export type ActionPriority = z.infer<typeof actionPrioritySchema>;
export type PhaseStatus = z.infer<typeof phaseStatusSchema>;
export type ReviewType = z.infer<typeof reviewTypeSchema>;
export type AssumptionSource = z.infer<typeof assumptionSourceSchema>;

// NEW type exports
export type HeroStats = z.infer<typeof heroStatsSchema>;
export type TaxSavingsHighlight = z.infer<typeof taxSavingsHighlightSchema>;
export type TaxSavingsItem = z.infer<typeof taxSavingsItemSchema>;
export type AIInsight = z.infer<typeof aiInsightSchema>;
export type AIInsightType = z.infer<typeof aiInsightTypeSchema>;
export type ScenarioComparison = z.infer<typeof scenarioComparisonSchema>;
export type Scenario = z.infer<typeof scenarioSchema>;
export type ScenarioMetrics = z.infer<typeof scenarioMetricsSchema>;
export type TimelineMilestone = z.infer<typeof timelineMilestoneSchema>;

// ============================================
// Validation Helpers
// ============================================

/**
 * Validate a raw LLM output against the schema (supports both v1 and v2)
 */
export function validateJourneyOutput(data: unknown): {
  valid: boolean;
  data?: JourneyOutput | JourneyOutputV1;
  version?: '1.0' | '2.0';
  errors?: string[];
} {
  // Try v2 first
  const resultV2 = journeyOutputSchema.safeParse(data);
  if (resultV2.success) {
    return { valid: true, data: resultV2.data, version: '2.0' };
  }

  // Fall back to v1
  const resultV1 = journeyOutputSchemaV1.safeParse(data);
  if (resultV1.success) {
    return { valid: true, data: resultV1.data, version: '1.0' };
  }

  // Both failed - return v2 errors (more detailed)
  return {
    valid: false,
    errors: resultV2.error.issues.map(
      (issue) => `${issue.path.join('.')}: ${issue.message}`
    ),
  };
}

/**
 * Check if journey output is v2 (has heroStats)
 */
export function isJourneyV2(data: JourneyOutput | JourneyOutputV1): data is JourneyOutput {
  return data.schemaVersion === '2.0' && 'heroStats' in data;
}

/**
 * Extract JSON from LLM response (handles markdown code blocks)
 */
export function extractJSONFromResponse(response: string): string | null {
  // Try to find JSON in markdown code block
  const codeBlockMatch = response.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (codeBlockMatch && codeBlockMatch[1]) {
    return codeBlockMatch[1].trim();
  }

  // Try to find raw JSON object
  const jsonMatch = response.match(/\{[\s\S]*\}/);
  if (jsonMatch && jsonMatch[0]) {
    return jsonMatch[0];
  }

  return null;
}

/**
 * Parse and validate LLM response
 */
export function parseJourneyResponse(response: string): {
  valid: boolean;
  data?: JourneyOutput | JourneyOutputV1;
  version?: '1.0' | '2.0';
  errors?: string[];
  rawJSON?: string;
} {
  const jsonStr = extractJSONFromResponse(response);

  if (!jsonStr) {
    return {
      valid: false,
      errors: ['No valid JSON found in response'],
    };
  }

  try {
    const parsed = JSON.parse(jsonStr);
    const validation = validateJourneyOutput(parsed);

    return {
      ...validation,
      rawJSON: jsonStr,
    };
  } catch (parseError) {
    return {
      valid: false,
      errors: [
        `JSON parse error: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`,
      ],
      rawJSON: jsonStr,
    };
  }
}
